import { useQueries, useQuery } from "@tanstack/react-query";
import {
	AlertTriangle,
	BarChart3,
	Loader2,
	Package,
	Users,
	X,
} from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import cacheKeys from "@/config/cache-keys";
import { computeProductDrift } from "@/lib/insuranceDrift";
import AppService from "@/services/AppService";
import type { APIResponse } from "@/types/APIresponse";
import type { Account } from "@/types/account/account";
import type { InsuranceProductDefinitionRaw } from "@/types/insurance/productDefinition";
import { productDefinitionsToTemplates } from "@/types/insurance/productDefinition";

const appService = new AppService();

export interface CatalogDriftStats {
	mainAccountsChecked: number;
	mainAccountsWithAnyDrift: number;
	totalDriftedProducts: number;
	totalOrphanProducts: number;
	productTypesWithDrift: number;
	topProductsByDrift: ReadonlyArray<{
		type: string;
		label: string;
		count: number;
	}>;
	topMissingParams: ReadonlyArray<{ param: string; count: number }>;
	topExtraParams: ReadonlyArray<{ param: string; count: number }>;
}

interface Props {
	definitions: InsuranceProductDefinitionRaw[];
	active: boolean;
	onClose: () => void;
	onDriftResolved?: (countByType: Map<string, number>) => void;
}

export const CatalogDriftSummary = ({
	definitions,
	active,
	onClose,
	onDriftResolved,
}: Props) => {
	const { t } = useTranslation();

	const { data: accountsData, isLoading: isLoadingAccounts } = useQuery<
		APIResponse<Account[]>
	>({
		queryKey: ["accounts", "all"],
		queryFn: () => appService.getAllAccounts(),
		enabled: active,
		staleTime: 1000 * 60 * 5,
	});

	const mainAccountIds = useMemo<string[]>(() => {
		if (!accountsData?.Result) return [];
		const seen = new Set<string>();
		const ids: string[] = [];
		for (const acc of accountsData.Result) {
			if (!seen.has(acc.main_account_id)) {
				seen.add(acc.main_account_id);
				ids.push(acc.main_account_id);
			}
		}
		return ids;
	}, [accountsData]);

	const treeQueries = useQueries({
		queries: mainAccountIds.map((id) => ({
			queryKey: cacheKeys.SINGLE_MAIN_ACCOUNT(id),
			queryFn: async () => appService.getMainAccountTree(id),
			enabled: active && mainAccountIds.length > 0,
			staleTime: 1000 * 60 * 5,
		})),
	});

	const templates = useMemo(
		() => productDefinitionsToTemplates(definitions),
		[definitions],
	);

	const isLoadingTrees =
		treeQueries.length > 0 && treeQueries.some((q) => q.isLoading);
	const isLoading =
		active && (isLoadingAccounts || isLoadingTrees || templates.length === 0);

	const emptyStats = useMemo(
		(): CatalogDriftStats => ({
			mainAccountsChecked: 0,
			mainAccountsWithAnyDrift: 0,
			totalDriftedProducts: 0,
			totalOrphanProducts: 0,
			productTypesWithDrift: 0,
			topProductsByDrift: [],
			topMissingParams: [],
			topExtraParams: [],
		}),
		[],
	);

	const stats = useMemo<CatalogDriftStats | null>(() => {
		if (!active || isLoading) return null;
		if (mainAccountIds.length === 0) return { ...emptyStats };

		if (treeQueries.length === 0) return null;

		let mainAccountsWithAnyDrift = 0;
		let totalDriftedProducts = 0;
		let totalOrphanProducts = 0;
		const countByType = new Map<string, number>();
		const missingFreq = new Map<string, number>();
		const extraFreq = new Map<string, number>();

		for (const q of treeQueries) {
			const tree = q.data?.Result;
			if (!tree) continue;
			const report = computeProductDrift(tree.insurances, templates);
			const hasIssue = report.drifted.length > 0 || report.orphan.length > 0;
			if (hasIssue) mainAccountsWithAnyDrift += 1;
			totalDriftedProducts += report.drifted.length;
			totalOrphanProducts += report.orphan.length;
			for (const d of report.drifted) {
				countByType.set(d.type, (countByType.get(d.type) ?? 0) + 1);
				for (const p of d.missingParams) {
					missingFreq.set(p, (missingFreq.get(p) ?? 0) + 1);
				}
				for (const p of d.extraParams) {
					extraFreq.set(p, (extraFreq.get(p) ?? 0) + 1);
				}
			}
		}

		const labelFor = (type: string) =>
			t(`admin.insurances.type-to-name.${type}`, { defaultValue: type });

		const topProductsByDrift = [...countByType.entries()]
			.filter(([, c]) => c > 0)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 8)
			.map(([type, count]) => ({
				type,
				label: labelFor(type),
				count,
			}));

		const sortFreq = (m: Map<string, number>) =>
			[...m.entries()]
				.sort((a, b) => b[1] - a[1])
				.slice(0, 8)
				.map(([param, count]) => ({ param, count }));

		return {
			mainAccountsChecked: mainAccountIds.length,
			mainAccountsWithAnyDrift,
			totalDriftedProducts,
			totalOrphanProducts,
			productTypesWithDrift: topProductsByDrift.length,
			topProductsByDrift,
			topMissingParams: sortFreq(missingFreq),
			topExtraParams: sortFreq(extraFreq),
		};
	}, [
		active,
		isLoading,
		treeQueries,
		templates,
		mainAccountIds.length,
		t,
		emptyStats,
	]);

	const driftResolvedSignature = useRef<string | null>(null);
	useLayoutEffect(() => {
		if (active) driftResolvedSignature.current = null;
	}, [active]);

	useEffect(() => {
		if (!active || isLoading || !onDriftResolved) return;

		if (mainAccountIds.length === 0) {
			const sig = "";
			if (driftResolvedSignature.current !== sig) {
				driftResolvedSignature.current = sig;
				onDriftResolved(new Map());
			}
			return;
		}

		if (treeQueries.length === 0) return;

		const countByType = new Map<string, number>();
		for (const q of treeQueries) {
			const tree = q.data?.Result;
			if (!tree) continue;
			const report = computeProductDrift(tree.insurances, templates);
			for (const d of report.drifted) {
				countByType.set(d.type, (countByType.get(d.type) ?? 0) + 1);
			}
		}
		const sig = JSON.stringify(
			[...countByType.entries()].sort((a, b) => a[0].localeCompare(b[0])),
		);
		if (driftResolvedSignature.current === sig) return;
		driftResolvedSignature.current = sig;
		onDriftResolved(countByType);
	}, [
		active,
		isLoading,
		treeQueries,
		templates,
		onDriftResolved,
		mainAccountIds.length,
	]);

	const completedCount = treeQueries.filter(
		(q) => q.isSuccess || q.isError,
	).length;
	const totalCount = mainAccountIds.length;

	if (!active) return null;

	return (
		<Card className="border-border shadow-sm">
			<CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-2">
				<div className="flex items-center gap-2 min-w-0">
					<BarChart3 className="h-5 w-5 shrink-0 text-muted-foreground" />
					<div className="min-w-0">
						<CardTitle className="text-base font-semibold leading-tight">
							{t("admin.product-definitions.catalog-scan.summary-title")}
						</CardTitle>
						<p className="text-xs text-muted-foreground mt-1">
							{t("admin.product-definitions.catalog-scan.summary-description")}
						</p>
					</div>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="shrink-0 h-8 w-8"
					onClick={onClose}
					aria-label={t("admin.product-definitions.catalog-scan.dismiss")}
				>
					<X className="h-4 w-4" />
				</Button>
			</CardHeader>
			<CardContent className="space-y-4 pt-0">
				{isLoading ? (
					<div className="flex flex-col items-center gap-3 py-10">
						<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
						<p className="text-sm text-muted-foreground text-center">
							{totalCount > 0
								? t("admin.product-definitions.drift-modal.loading-progress", {
										done: completedCount,
										total: totalCount,
									})
								: t("admin.product-definitions.drift-modal.loading")}
						</p>
					</div>
				) : stats === null ? null : stats.mainAccountsChecked === 0 ? (
					<div className="flex flex-col items-center gap-2 py-8 text-center rounded-md border bg-muted/20 px-4">
						<p className="text-sm font-medium">
							{t("admin.product-definitions.catalog-scan.no-main-accounts")}
						</p>
						<p className="text-xs text-muted-foreground max-w-md">
							{t(
								"admin.product-definitions.catalog-scan.no-main-accounts-description",
							)}
						</p>
					</div>
				) : stats.mainAccountsWithAnyDrift === 0 &&
					stats.totalOrphanProducts === 0 ? (
					<div className="flex flex-col items-center gap-2 py-8 text-center rounded-md border border-moss-green-80/20 bg-moss-green-80/5">
						<p className="text-sm font-medium">
							{t("admin.product-definitions.drift-modal.no-drift")}
						</p>
						<p className="text-xs text-muted-foreground max-w-md">
							{t(
								"admin.product-definitions.catalog-scan.no-drift-catalog-scope",
							)}
						</p>
					</div>
				) : (
					<>
						<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
							<div className="rounded-lg border bg-muted/30 px-3 py-2.5">
								<div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
									<Users className="h-3.5 w-3.5" />
									{t("admin.product-definitions.catalog-scan.stat-checked")}
								</div>
								<p className="text-2xl font-semibold tabular-nums mt-1">
									{stats.mainAccountsChecked}
								</p>
							</div>
							<div className="rounded-lg border bg-muted/30 px-3 py-2.5">
								<div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
									<AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
									{t(
										"admin.product-definitions.catalog-scan.stat-customers-drift",
									)}
								</div>
								<p className="text-2xl font-semibold tabular-nums mt-1 text-amber-700 dark:text-amber-500">
									{stats.mainAccountsWithAnyDrift}
								</p>
							</div>
							<div className="rounded-lg border bg-muted/30 px-3 py-2.5">
								<div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
									<Package className="h-3.5 w-3.5" />
									{t(
										"admin.product-definitions.catalog-scan.stat-product-rows",
									)}
								</div>
								<p className="text-2xl font-semibold tabular-nums mt-1">
									{stats.totalDriftedProducts}
								</p>
							</div>
							<div className="rounded-lg border bg-muted/30 px-3 py-2.5">
								<div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
									{t(
										"admin.product-definitions.catalog-scan.stat-types-affected",
									)}
								</div>
								<p className="text-2xl font-semibold tabular-nums mt-1">
									{stats.productTypesWithDrift}
								</p>
							</div>
						</div>

						{stats.totalOrphanProducts > 0 && (
							<div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
								<AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
								<div>
									<p className="font-medium text-destructive">
										{t("admin.product-definitions.catalog-scan.orphan-heading")}
									</p>
									<p className="text-xs text-muted-foreground mt-0.5">
										{t("admin.product-definitions.catalog-scan.orphan-count", {
											count: stats.totalOrphanProducts,
										})}
									</p>
								</div>
							</div>
						)}

						<div className="grid gap-4 sm:grid-cols-2">
							<div className="space-y-2">
								<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
									{t("admin.product-definitions.catalog-scan.top-products")}
								</p>
								<ul className="space-y-1.5">
									{stats.topProductsByDrift.map((row) => (
										<li
											key={row.type}
											className="flex items-center justify-between gap-2 text-sm"
										>
											<span className="truncate min-w-0" title={row.type}>
												{row.label}
											</span>
											<Badge variant="secondary" className="tabular-nums">
												{row.count}
											</Badge>
										</li>
									))}
								</ul>
							</div>
							<div className="space-y-3">
								<div className="space-y-2">
									<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
										{t("admin.product-definitions.catalog-scan.top-missing")}
									</p>
									{stats.topMissingParams.length === 0 ? (
										<p className="text-xs text-muted-foreground italic">—</p>
									) : (
										<ul className="space-y-1.5">
											{stats.topMissingParams.map((row) => (
												<li
													key={row.param}
													className="flex items-center justify-between gap-2 text-sm"
												>
													<code className="text-xs truncate min-w-0">
														{row.param}
													</code>
													<span className="text-xs text-muted-foreground tabular-nums shrink-0">
														×{row.count}
													</span>
												</li>
											))}
										</ul>
									)}
								</div>
								<div className="space-y-2">
									<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
										{t("admin.product-definitions.catalog-scan.top-extra")}
									</p>
									{stats.topExtraParams.length === 0 ? (
										<p className="text-xs text-muted-foreground italic">—</p>
									) : (
										<ul className="space-y-1.5">
											{stats.topExtraParams.map((row) => (
												<li
													key={row.param}
													className="flex items-center justify-between gap-2 text-sm"
												>
													<code className="text-xs truncate min-w-0">
														{row.param}
													</code>
													<span className="text-xs text-muted-foreground tabular-nums shrink-0">
														×{row.count}
													</span>
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
};
